/**
 * React Hooks for Zustand App Store
 *
 * Provides React hooks to interact with the Zustand app store,
 * enabling seamless integration between Zustand and React components.
 *
 * Features:
 * - Type-safe hooks
 * - Selective subscriptions for performance
 * - Navigation hook combining store state with TanStack Router
 * - Backward compatibility with existing AppContext API
 * - Optimized re-renders
 */

import { useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { Organization } from "@/services/organization";
import {
	isPlaceholderOrgId,
	resolveConcreteOrgId,
} from "@/utils/web-org-redirect";
import { type AppStore, type AppType, appStore } from "./app-store";
import { authStore } from "./auth-store";

// ============================================================================
// CORE HOOKS
// ============================================================================

/**
 * Main app hook - returns full app state and actions
 * Use this for components that need multiple app properties
 */
export const useAppStore = (): AppStore => {
	return useStore(appStore);
};

/**
 * Selective app state hook - only subscribe to specific properties
 * Use this for performance optimization when you only need specific values
 */
export const useAppSelector = <T>(selector: (state: AppStore) => T): T => {
	return useStore(appStore, selector);
};

// ============================================================================
// SELECTIVE HOOKS (Optimized)
// ============================================================================

/**
 * Current app hook - only subscribes to current app type
 * Optimized for components that only care about which app is active
 */
export const useCurrentApp = (): AppType => {
	return useStore(appStore, (state) => state.currentApp);
};

/**
 * Current organization hook - only subscribes to organization data
 * Optimized for components that only need organization information
 */
export const useCurrentOrganization = (): Organization | null => {
	return useStore(appStore, (state) => state.currentOrganization);
};

/**
 * Available organizations hook - only subscribes to organizations list
 */
export const useAvailableOrganizations = (): Organization[] => {
	return useStore(appStore, (state) => state.availableOrganizations);
};

/**
 * App permissions hook - returns permission-related derived state
 * Optimized for components that need permission checks
 */
export const useAppPermissions = () => {
	return useStore(
		appStore,
		useShallow((state) => ({
			canAccessAdmin: state.getCanAccessAdmin(),
			canAccessMultipleOrgs: state.getCanAccessMultipleOrgs(),
		})),
	);
};

/**
 * App type checks hook - returns boolean checks for current app
 * Optimized for conditional rendering based on app type
 */
export const useAppTypeChecks = () => {
	return useStore(
		appStore,
		useShallow((state) => ({
			isMainApp: state.currentApp === "main",
			isAdminApp: state.currentApp === "admin",
			isOrgAdminApp: state.currentApp === "org-admin",
		})),
	);
};

// ============================================================================
// ACTION HOOKS
// ============================================================================

/**
 * App actions hook - returns only the action functions
 * Use this for components that only need to trigger app actions
 */
export const useAppActions = () => {
	return useStore(
		appStore,
		useShallow((state) => ({
			setApp: state.setApp,
			setOrganization: state.setOrganization,
			setAvailableOrganizations: state.setAvailableOrganizations,
			reset: state.reset,
		})),
	);
};

// ============================================================================
// NAVIGATION HOOK
// ============================================================================

/**
 * Return type for useAppNavigation hook
 */
export interface UseAppNavigationReturn {
	/**
	 * Switch to a different app with navigation
	 * Updates store state AND navigates to the app's root route
	 */
	switchToApp: (app: AppType) => void;

	/**
	 * Switch to an organization with navigation
	 * Updates store and navigates by current route: /web/ and /org-admin/ swap org in path;
	 * /admin/ → /org-admin/{orgId}/home; otherwise /web/{orgId}/home
	 */
	switchToOrganization: (orgId: string) => void;

	/** Current app type */
	currentApp: AppType;

	/** Current organization */
	currentOrganization: Organization | null;
}

/**
 * Hook for type-safe app navigation
 *
 * Combines state management (via app store) with navigation (via TanStack Router).
 * Routes are validated at compile time by TypeScript.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { switchToApp, switchToOrganization } = useAppNavigation();
 *
 *   return (
 *     <button onClick={() => switchToApp("admin")}>
 *       Go to Admin
 *     </button>
 *   );
 * }
 * ```
 */
export function useAppNavigation(): UseAppNavigationReturn {
	const router = useRouter();
	const currentApp = useCurrentApp();
	const currentOrganization = useCurrentOrganization();
	const { setApp, setOrganization } = useAppActions();

	/**
	 * Switch to a different app (main, admin, or org-admin)
	 *
	 * 1. Updates app store state
	 * 2. Navigates to the app's root route using TanStack Router
	 *
	 * Routes are type-safe - TypeScript validates them at compile time!
	 */
	const switchToApp = useCallback(
		(app: AppType) => {
			// Check permissions for admin app
			const canAccessAdmin = appStore.getState().getCanAccessAdmin();
			if ((app === "admin" || app === "org-admin") && !canAccessAdmin) {
				console.warn(
					"⚠️ AppNavigation: User does not have permission to access admin app",
				);
				return;
			}

			// Step 1: Update state
			setApp(app);

			// Step 2: Navigate to app root with view transitions
			// TanStack Router validates these routes at compile time
			if (app === "main") {
				const state = appStore.getState();
				const currentOrgId = resolveConcreteOrgId({
					storeOrgId: state.currentOrganization?.id,
					activeOrganizationId: authStore.getState().activeOrganizationId,
					firstAvailableOrgId: state.availableOrganizations.find(
						(org) => !isPlaceholderOrgId(org.id),
					)?.id,
					availableOrganizations: state.availableOrganizations,
				});

				if (currentOrgId) {
					authStore.getState().setActiveOrganization(currentOrgId);
					router.navigate({
						to: `/web/${currentOrgId}/home`,
						viewTransition: {
							types: ["app-switch", "slide-left"],
						},
					});
				}
			} else if (app === "admin") {
				const state = appStore.getState();
				const orgId = resolveConcreteOrgId({
					storeOrgId: state.currentOrganization?.id,
					activeOrganizationId: authStore.getState().activeOrganizationId,
					firstAvailableOrgId: state.availableOrganizations.find(
						(org) => !isPlaceholderOrgId(org.id),
					)?.id,
					availableOrganizations: state.availableOrganizations,
				});
				if (orgId) {
					setOrganization(orgId);
					authStore.getState().setActiveOrganization(orgId);
					router.navigate({
						to: `/org-admin/${orgId}/home`,
						viewTransition: {
							types: ["app-switch", "slide-right"],
						},
					});
				} else {
					router.navigate({
						to: "/admin/home",
						viewTransition: {
							types: ["app-switch", "slide-right"],
						},
					});
				}
			} else if (app === "org-admin") {
				const state = appStore.getState();
				const orgId = resolveConcreteOrgId({
					storeOrgId: state.currentOrganization?.id,
					activeOrganizationId: authStore.getState().activeOrganizationId,
					firstAvailableOrgId: state.availableOrganizations.find(
						(org) => !isPlaceholderOrgId(org.id),
					)?.id,
					availableOrganizations: state.availableOrganizations,
				});
				if (orgId) {
					authStore.getState().setActiveOrganization(orgId);
					router.navigate({
						to: `/org-admin/${orgId}/home`,
						viewTransition: {
							types: ["app-switch", "slide-right"],
						},
					});
				}
			}

			console.log(`🧭 AppNavigation: Switched to app "${app}"`);
		},
		[router, setApp, setOrganization],
	);

	/**
	 * Switch to an organization
	 *
	 * 1. Updates app store state to select the organization
	 * 2. Updates auth store active organization
	 * 3. Navigates using current route: /web/ and /org-admin/ swap the org segment in the path;
	 *    /admin/* → /org-admin/{orgId}/home; otherwise defaults to /web/{orgId}/home
	 */
	const switchToOrganization = useCallback(
		(orgId: string) => {
			setOrganization(orgId);
			authStore.getState().setActiveOrganization(orgId);

			if (orgId === "all") {
				router.navigate({
					to: "/admin/home",
					viewTransition: {
						types: ["org-switch", "fade"],
					},
				});
				return;
			}

			// Read the pathname live from the router so we always use the actual
			// current location, not a potentially stale store value.
			const pathname = router.state.location.pathname;
			const isInWebApp = pathname.startsWith("/web/");

			if (isInWebApp) {
				router.navigate({
					to: `/web/${orgId}/home`,
					viewTransition: {
						types: ["org-switch", "fade"],
					},
				});
				return;
			}

			router.navigate({
				to: `/org-admin/${orgId}/home`,
				viewTransition: {
					types: ["org-switch", "fade"],
				},
			});
		},
		[router, setOrganization],
	);

	return {
		switchToApp,
		switchToOrganization,
		currentApp,
		currentOrganization,
	};
}

// ============================================================================
// BACKWARD COMPATIBILITY HOOKS
// ============================================================================

/**
 * Backward compatibility hook - matches the old AppContext API
 * This allows existing components to work without changes
 */
export const useAppContext = () => {
	const state = useStore(appStore);

	return {
		// State
		currentApp: state.currentApp,
		currentOrganization: state.currentOrganization,
		availableOrganizations: state.availableOrganizations,

		// Permissions
		canAccessAdmin: state.getCanAccessAdmin(),
		canAccessMultipleOrgs: state.getCanAccessMultipleOrgs(),

		// Actions (renamed to match old API)
		switchToApp: state.setApp,
		selectOrganization: state.setOrganization,

		// Utilities
		isMainApp: state.currentApp === "main",
		isAdminApp: state.currentApp === "admin",
	};
};

// ============================================================================
// RENDER HELPERS
// ============================================================================

/**
 * Custom hook for conditional rendering based on app state
 * Returns render helpers for common app scenarios
 */
export const useAppRender = () => {
	const currentApp = useCurrentApp();

	return {
		// Render helpers
		showMainApp: currentApp === "main",
		showAdminApp: currentApp === "admin",
		showOrgAdminApp: currentApp === "org-admin",
		showAnyAdminApp: currentApp === "admin" || currentApp === "org-admin",

		// Current app
		currentApp,
	};
};

// ============================================================================
// DEBUG HOOK
// ============================================================================

/**
 * Development/debugging hook
 * Provides additional utilities for development
 */
export const useAppDebug = () => {
	const state = useStore(appStore);

	return {
		// Full state for debugging
		fullState: state,

		// Quick state summary
		stateSummary: {
			currentApp: state.currentApp,
			currentOrganization: state.currentOrganization?.name ?? "None",
			availableOrgsCount: state.availableOrganizations.length,
			canAccessAdmin: state.getCanAccessAdmin(),
			canAccessMultipleOrgs: state.getCanAccessMultipleOrgs(),
		},

		// Actions for testing
		testActions: {
			reset: state.reset,
			setApp: state.setApp,
			setOrganization: state.setOrganization,
		},
	};
};
